import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Save, Check, Clock, MapPin, Send, CalendarDays,
  Phone, Mail, User, Link2, X, ClipboardList, Undo2,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import DatePickerDialog from "@/components/DatePickerDialog";
import { BRAND_MONOGRAM } from "@/lib/brand";

const emptyPauta = {
  retranca: "Nova pauta", responsavel: "", horario: "", endereco: "",
  entrevistados: [], recados: "", links: [], informacoes: "",
  compromissos_extras: [], approved: false,
};

export default function ProducaoPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [board, setBoard] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dateAction, setDateAction] = useState(null); // {mode: 'move'|'send', pautaId}
  const [historyVersion, setHistoryVersion] = useState(0);
  const historyRef = useRef({}); // { pautaId: [snapshot, ...] }
  const saveTimer = useRef(null);

  const load = async () => {
    const r = await api.get(`/production/${id}`);
    setBoard(r.data);
    if (!selectedId && r.data.pautas.length > 0) {
      setSelectedId(r.data.pautas[0].id);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const selected = useMemo(
    () => board?.pautas.find((p) => p.id === selectedId) || null,
    [board, selectedId]
  );

  // Inject client-side stable keys for nested array items so React can diff them correctly
  const _withKeys = (arr) => (arr || []).map((it) => (it && it._key ? it : { ...it, _key: crypto.randomUUID() }));

  useEffect(() => {
    if (selected) {
      setDraft({
        ...selected,
        entrevistados: _withKeys(selected.entrevistados),
        links: _withKeys(selected.links),
        compromissos_extras: _withKeys(selected.compromissos_extras),
      });
    } else {
      setDraft(null);
    }
  }, [selectedId]); // eslint-disable-line

  const persistDraft = async (next, opts = {}) => {
    if (!next || !board) return;
    if (!opts.skipHistory) {
      const prev = board.pautas.find((p) => p.id === next.id);
      if (prev) {
        const arr = historyRef.current[next.id] || [];
        arr.push(prev);
        if (arr.length > 20) arr.shift();
        historyRef.current[next.id] = arr;
        setHistoryVersion((v) => v + 1);
      }
    }
    setSaving(true);
    try {
      const r = await api.patch(`/production/${board.id}/pautas/${next.id}`, {
        retranca: next.retranca, responsavel: next.responsavel,
        horario: next.horario, endereco: next.endereco,
        entrevistados: next.entrevistados, recados: next.recados,
        links: next.links, informacoes: next.informacoes,
        compromissos_extras: next.compromissos_extras || [],
        approved: next.approved,
      });
      setBoard(r.data);
    } finally {
      setSaving(false);
    }
  };

  const undoPauta = async () => {
    if (!selectedId) return;
    const arr = historyRef.current[selectedId] || [];
    if (arr.length === 0) {
      toast.message("Nada para desfazer.");
      return;
    }
    const prev = arr.pop();
    setHistoryVersion((v) => v + 1);
    setDraft({ ...prev });
    if (saveTimer.current) clearTimeout(saveTimer.current);
    await persistDraft(prev, { skipHistory: true });
    toast.success("Alteração desfeita.");
  };

  const onDraftChange = (patch) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistDraft(next), 500);
  };

  const addPauta = async () => {
    const r = await api.post(`/production/${board.id}/pautas`, emptyPauta);
    setBoard(r.data);
    const last = r.data.pautas[r.data.pautas.length - 1];
    setSelectedId(last.id);
  };

  const deletePauta = async (pid) => {
    if (!window.confirm("Excluir esta pauta?")) return;
    const r = await api.delete(`/production/${board.id}/pautas/${pid}`);
    setBoard(r.data);
    if (selectedId === pid) setSelectedId(r.data.pautas[0]?.id || null);
  };

  const runDateAction = async (targetDate) => {
    if (!dateAction || !board) return;
    const { mode, pautaId } = dateAction;
    try {
      if (mode === "move") {
        const r = await api.post(
          `/production/${board.id}/pautas/${pautaId}/move-to`,
          { date: targetDate }
        );
        setBoard(r.data);
        if (selectedId === pautaId) setSelectedId(r.data.pautas[0]?.id || null);
        toast.success(`Pauta movida para ${targetDate}.`);
      } else if (mode === "send") {
        await api.post(
          `/production/${board.id}/pautas/${pautaId}/send-to-espelho`,
          { date: targetDate }
        );
        toast.success(`Pauta enviada ao espelho de ${targetDate}.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Operação falhou.");
    } finally {
      setDateAction(null);
    }
  };

  // Entrevistados helpers
  const addEntrevistado = () => {
    onDraftChange({
      entrevistados: [...(draft.entrevistados || []), { nome: "", telefone: "", email: "", _key: crypto.randomUUID() }],
    });
  };
  const updateEntrevistado = (idx, patch) => {
    const arr = [...(draft.entrevistados || [])];
    arr[idx] = { ...arr[idx], ...patch };
    onDraftChange({ entrevistados: arr });
  };
  const removeEntrevistado = (idx) => {
    const arr = [...(draft.entrevistados || [])];
    arr.splice(idx, 1);
    onDraftChange({ entrevistados: arr });
  };

  const addLink = () => {
    onDraftChange({ links: [...(draft.links || []), { label: "", url: "", _key: crypto.randomUUID() }] });
  };
  const updateLink = (idx, patch) => {
    const arr = [...(draft.links || [])];
    arr[idx] = { ...arr[idx], ...patch };
    onDraftChange({ links: arr });
  };
  const removeLink = (idx) => {
    const arr = [...(draft.links || [])];
    arr.splice(idx, 1);
    onDraftChange({ links: arr });
  };

  const addCompromisso = () => {
    onDraftChange({
      compromissos_extras: [
        ...(draft.compromissos_extras || []),
        { horario: "", endereco: "", informacoes: "", _key: crypto.randomUUID() },
      ],
    });
  };
  const updateCompromisso = (idx, patch) => {
    const arr = [...(draft.compromissos_extras || [])];
    arr[idx] = { ...arr[idx], ...patch };
    onDraftChange({ compromissos_extras: arr });
  };
  const removeCompromisso = (idx) => {
    const arr = [...(draft.compromissos_extras || [])];
    arr.splice(idx, 1);
    onDraftChange({ compromissos_extras: arr });
  };

  if (!board) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500">Carregando...</div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost" size="sm" data-testid="back-to-calendar"
              onClick={() => nav("/")}
              className="rounded-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Calendário
            </Button>
            <img
              src={BRAND_MONOGRAM}
              alt="PodiUmScript" className="h-9 w-auto"
            />
            <div className="border-l border-zinc-200 pl-4">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-600 flex items-center gap-1.5">
                <ClipboardList className="w-3 h-3" /> Produção
              </div>
              <div className="font-heading text-lg font-bold tracking-tight">
                {format(parseISO(board.date), "EEEE, dd 'de' MMMM", { locale: ptBR })}
              </div>
            </div>
          </div>
          <div className="text-xs text-zinc-400">
            {saving ? "salvando..." : <span className="inline-flex items-center gap-1"><Save className="w-3 h-3" />salvo</span>}
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* Left: pautas list */}
        <aside>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-zinc-500">
              Pautas ({board.pautas.length})
            </div>
            <Button
              size="sm" data-testid="add-pauta-btn"
              onClick={addPauta}
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-8"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Nova
            </Button>
          </div>

          {board.pautas.length === 0 ? (
            <Card className="rounded-sm border-dashed border-zinc-300 shadow-none p-8 text-center text-sm text-zinc-500">
              Nenhuma pauta ainda.
            </Card>
          ) : (
            <div className="space-y-2">
              {board.pautas.map((p) => (
                <button
                  key={p.id}
                  data-testid={`pauta-${p.id}`}
                  onClick={() => setSelectedId(p.id)}
                  className={`w-full text-left p-3 border rounded-sm ${p.id === selectedId ? "border-amber-500 ring-1 ring-amber-500" : "border-zinc-200 hover:border-zinc-400"} ${p.approved ? "bg-emerald-50/50" : "bg-white"}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {p.approved && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-bold bg-emerald-600 text-white flex items-center gap-1">
                        <Check className="w-3 h-3" /> aprovada
                      </span>
                    )}
                    {p.horario && (
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {p.horario}
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-sm text-zinc-900 truncate">
                    {p.retranca || "Sem título"}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 truncate">
                    {p.responsavel && <span>🎙 {p.responsavel}</span>}
                    {p.endereco && <span> · 📍 {p.endereco}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Right: editor */}
        <section>
          {!draft ? (
            <Card className="rounded-sm border-dashed border-zinc-300 shadow-none p-16 text-center">
              <ClipboardList className="w-8 h-8 text-zinc-400 mx-auto mb-3" />
              <div className="font-heading text-2xl font-bold tracking-tight">Selecione uma pauta</div>
              <p className="text-zinc-500 mt-2">Ou crie uma nova.</p>
            </Card>
          ) : (
            <Card className={`rounded-sm shadow-none ${draft.approved ? "border-emerald-500 bg-emerald-50/30" : "border-zinc-200"}`}>
              {/* Top actions bar */}
              <div className="p-4 border-b border-zinc-100 flex items-center justify-between gap-2 flex-wrap bg-white">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={draft.approved}
                    onCheckedChange={(v) => onDraftChange({ approved: !!v })}
                    data-testid="pauta-approved-checkbox"
                    className="rounded-sm data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                  />
                  <span className={`text-[11px] uppercase tracking-wider font-bold ${draft.approved ? "text-emerald-700" : "text-zinc-600"}`}>
                    {draft.approved ? "Aprovada" : "Aprovar"}
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm" variant="outline"
                    data-testid="undo-pauta-btn"
                    onClick={undoPauta}
                    disabled={!(historyRef.current[selectedId] || []).length}
                    className="rounded-sm h-8 text-zinc-600 border-zinc-300"
                    title="Desfazer última alteração salva"
                  >
                    <Undo2 className="w-3.5 h-3.5 mr-1.5" />
                    Desfazer
                    {(historyRef.current[selectedId] || []).length > 0 && (
                      <span className="ml-1.5 text-[10px] text-zinc-500">
                        ({(historyRef.current[selectedId] || []).length})
                      </span>
                    )}
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    data-testid="move-pauta-btn"
                    onClick={() => setDateAction({ mode: "move", pautaId: draft.id })}
                    className="rounded-sm h-8"
                  >
                    <CalendarDays className="w-3.5 h-3.5 mr-1.5" /> Mudar data
                  </Button>
                  <Button
                    size="sm"
                    data-testid="send-to-espelho-btn"
                    onClick={() => setDateAction({ mode: "send", pautaId: draft.id })}
                    className="rounded-sm bg-[#152D4B] hover:bg-[#0E1F36] text-white h-8"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Enviar ao espelho
                  </Button>
                  <button
                    data-testid="delete-pauta-btn"
                    onClick={() => deletePauta(draft.id)}
                    className="text-zinc-300 hover:text-red-600 flex items-center p-1"
                    title="Excluir pauta"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Basic fields */}
              <div className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Retranca</Label>
                  <Input
                    data-testid="pauta-retranca"
                    value={draft.retranca}
                    onChange={(e) => onDraftChange({ retranca: e.target.value })}
                    className="rounded-sm border-zinc-300 text-base font-semibold"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Responsável</Label>
                  <Input
                    data-testid="pauta-responsavel"
                    value={draft.responsavel}
                    onChange={(e) => onDraftChange({ responsavel: e.target.value })}
                    className="rounded-sm border-zinc-300"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Recados</Label>
                  <Textarea
                    data-testid="pauta-recados"
                    value={draft.recados}
                    onChange={(e) => onDraftChange({ recados: e.target.value })}
                    rows={3}
                    placeholder="Recados internos, avisos, alertas..."
                    className="rounded-sm border-zinc-300 resize-none"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Horário
                    </Label>
                    <Input
                      type="time"
                      data-testid="pauta-horario"
                      value={draft.horario}
                      onChange={(e) => onDraftChange({ horario: e.target.value })}
                      className="rounded-sm border-zinc-300"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> Endereço
                    </Label>
                    <Input
                      data-testid="pauta-endereco"
                      value={draft.endereco}
                      onChange={(e) => onDraftChange({ endereco: e.target.value })}
                      className="rounded-sm border-zinc-300"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Informações</Label>
                  <Textarea
                    data-testid="pauta-informacoes"
                    value={draft.informacoes}
                    onChange={(e) => onDraftChange({ informacoes: e.target.value })}
                    rows={5}
                    placeholder="Contexto da matéria, apuração, angulação, referências..."
                    className="rounded-sm border-zinc-300 leading-relaxed resize-none"
                  />
                </div>

                {/* Compromissos extras */}
                {(draft.compromissos_extras || []).map((c, i) => (
                  <div key={c._key || i} data-testid={`compromisso-extra-${i}`} className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/40 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                        Compromisso {i + 2}
                      </div>
                      <button
                        data-testid={`remove-compromisso-${i}`}
                        onClick={() => removeCompromisso(i)}
                        className="text-zinc-400 hover:text-red-600"
                        title="Remover compromisso"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Horário
                        </Label>
                        <Input
                          type="time"
                          data-testid={`compromisso-horario-${i}`}
                          value={c.horario || ""}
                          onChange={(e) => updateCompromisso(i, { horario: e.target.value })}
                          className="rounded-sm border-zinc-300"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> Endereço
                        </Label>
                        <Input
                          data-testid={`compromisso-endereco-${i}`}
                          value={c.endereco || ""}
                          onChange={(e) => updateCompromisso(i, { endereco: e.target.value })}
                          className="rounded-sm border-zinc-300"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Informações</Label>
                      <Textarea
                        data-testid={`compromisso-informacoes-${i}`}
                        value={c.informacoes || ""}
                        onChange={(e) => updateCompromisso(i, { informacoes: e.target.value })}
                        rows={3}
                        className="rounded-sm border-zinc-300 leading-relaxed resize-none"
                      />
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="add-compromisso-btn"
                  onClick={addCompromisso}
                  className="rounded-sm border-dashed border-zinc-400 text-zinc-600 hover:text-[#152D4B] hover:border-[#152D4B] w-full h-9"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Adicionar horário / endereço / informações
                </Button>
              </div>

              {/* Entrevistados */}
              <div className="p-6 border-t border-zinc-100 bg-zinc-50/50">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 flex items-center gap-1">
                    <User className="w-3 h-3" /> Entrevistados
                  </Label>
                  <Button
                    size="sm" variant="outline"
                    data-testid="add-entrevistado-btn"
                    onClick={addEntrevistado}
                    className="rounded-sm h-7 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Adicionar
                  </Button>
                </div>
                <div className="space-y-2">
                  {(draft.entrevistados || []).map((e, i) => (
                    <div key={e._key || i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 bg-white p-2 border border-zinc-200 rounded-sm">
                      <Input
                        value={e.nome} onChange={(ev) => updateEntrevistado(i, { nome: ev.target.value })}
                        placeholder="Nome"
                        data-testid={`entrevistado-nome-${i}`}
                        className="rounded-sm border-zinc-300 h-9 text-sm"
                      />
                      <Input
                        value={e.telefone} onChange={(ev) => updateEntrevistado(i, { telefone: ev.target.value })}
                        placeholder="Telefone"
                        data-testid={`entrevistado-tel-${i}`}
                        className="rounded-sm border-zinc-300 h-9 text-sm"
                      />
                      <Input
                        value={e.email} onChange={(ev) => updateEntrevistado(i, { email: ev.target.value })}
                        placeholder="E-mail"
                        data-testid={`entrevistado-email-${i}`}
                        className="rounded-sm border-zinc-300 h-9 text-sm"
                      />
                      <button
                        data-testid={`remove-entrevistado-${i}`}
                        onClick={() => removeEntrevistado(i)}
                        className="text-zinc-400 hover:text-red-600 px-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {(!draft.entrevistados || draft.entrevistados.length === 0) && (
                    <div className="text-xs text-zinc-500 italic">Nenhum entrevistado adicionado.</div>
                  )}
                </div>
              </div>

              {/* Links */}
              <div className="p-6 border-t border-zinc-100 bg-zinc-50/50">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 flex items-center gap-1">
                    <Link2 className="w-3 h-3" /> Links importantes
                  </Label>
                  <Button
                    size="sm" variant="outline"
                    data-testid="add-link-btn"
                    onClick={addLink}
                    className="rounded-sm h-7 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Adicionar
                  </Button>
                </div>
                <div className="space-y-2">
                  {(draft.links || []).map((l, i) => (
                    <div key={l._key || i} className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-2 bg-white p-2 border border-zinc-200 rounded-sm">
                      <Input
                        value={l.label} onChange={(ev) => updateLink(i, { label: ev.target.value })}
                        placeholder="Legenda"
                        data-testid={`link-label-${i}`}
                        className="rounded-sm border-zinc-300 h-9 text-sm"
                      />
                      <Input
                        value={l.url} onChange={(ev) => updateLink(i, { url: ev.target.value })}
                        placeholder="https://..."
                        data-testid={`link-url-${i}`}
                        className="rounded-sm border-zinc-300 h-9 text-sm font-mono"
                      />
                      <div className="flex items-center gap-1">
                        {l.url && (
                          <a
                            href={l.url} target="_blank" rel="noopener noreferrer"
                            className="text-[#152D4B] hover:underline text-xs px-2 h-9 flex items-center"
                          >
                            abrir
                          </a>
                        )}
                        <button
                          data-testid={`remove-link-${i}`}
                          onClick={() => removeLink(i)}
                          className="text-zinc-400 hover:text-red-600 px-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {(!draft.links || draft.links.length === 0) && (
                    <div className="text-xs text-zinc-500 italic">Nenhum link salvo.</div>
                  )}
                </div>
              </div>

              {/* Informações moved to top; extras use the "Adicionar" button above */}
            </Card>
          )}
        </section>
      </main>

      <DatePickerDialog
        open={!!dateAction}
        onOpenChange={(o) => { if (!o) setDateAction(null); }}
        title={dateAction?.mode === "move" ? "Mudar data da pauta" : "Enviar pauta ao espelho de..."}
        description={
          dateAction?.mode === "move"
            ? "A pauta será transferida para a data escolhida (a pauta atual sai desta data)."
            : "Uma cópia da pauta será enviada ao espelho de edição da data escolhida, com todas as informações."
        }
        confirmLabel={dateAction?.mode === "move" ? "Mover" : "Enviar"}
        onConfirm={runDateAction}
      />
    </div>
  );
}
